import { Form } from "antd";
import { FormattedMessage } from "react-intl";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SharedContextProps } from "@lib/types/Context";
import { Customer } from "@lib/types/Customer";
import { convertNumericFields } from "@lib/types/General";
import { useCustomerQueries } from "@lib/apiQueries";
import { useApi } from "@lib/hooks/APIHooks";
import { useMutation } from "@tanstack/react-query";
import { apiUpdateCustomer } from "@lib/api/customer";
import { toast } from "react-toastify";
import useIntl from "react-intl/lib/src/components/useIntl";
import CustomerSettings from "./CustomerSettings";
import AISettings from "./AISettings";
import AppConfig from "./AppConfig";
import LogoSettings from "./LogoSettings";
import APISettings from "./APISettings";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { Space } from "antd";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { AppCard, AppTabs } from "@lib/components/AppComponents";

const CustomerDetailEdit: React.FC<SharedContextProps> = ({
  customerId,
  customer: initialCustomer,
  context: _context,
}) => {
  const { messages } = useIntl();
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState<string>("customerSettings");

  const { queriesCacheClear } = useApi();

  const { customerQuery } = useCustomerQueries({
    id: customerId,
    messages,
    queryKey: ["customer", customerId],
  });

  const { data, refetch } = customerQuery;
  const customer = initialCustomer || data?.data;

  const updateCustomerMutation = useMutation({
    mutationFn: apiUpdateCustomer,
    onSuccess() {
      toast.success(
        (messages["common.updatedMessage"] as string).replace(
          "{name}",
          messages["common.customer"] as string,
        ),
      );
      refetch();
      queriesCacheClear({ searchKeys: ["customer"] });
    },
  });

  const { mutate, isPending } = updateCustomerMutation;

  const initialValues = useMemo(() => {
    return customer
      ? convertNumericFields(customer)
      : ({
          name: "",
          domain: "",
          logoUrl: "",
          enabled: true,
          isClosed: false,
          isAutoBtcEnabled: false,
          isWhatsappPlatform: false,
          isMarketPlaceEnabled: false,
          aiServicesMargin: null,
        } as Customer);
  }, [customer]);

  const handleResetForm = useCallback(() => {
    form?.setFieldsValue(initialValues);
  }, [initialValues, form]);

  useEffect(() => {
    if (!customer) return;
    handleResetForm();
  }, [customer, handleResetForm]);

  const tabItems = [
    {
      key: "customerSettings",
      label: <FormattedMessage id="customer.tabs.customerSettings" />,
      children: CustomerSettings,
    },
    {
      key: "apiSettings",
      label: <FormattedMessage id="customer.tabs.apiSettings" />,
      children: APISettings,
    },
    {
      key: "logoSettings",
      label: <FormattedMessage id="customer.tabs.logoSettings" />,
      children: LogoSettings,
    },
    {
      key: "aiSettings",
      label: <FormattedMessage id="customer.tabs.aiSettings" />,
      children: AISettings,
    },
    {
      key: "appConfig",
      label: <FormattedMessage id="customer.tabs.appConfig" />,
      children: AppConfig,
    },
  ];

  return (
    <AppCard cardBg={true} borderFull coloredHeader>
      <Form<Customer>
        form={form}
        name={"customer-detail-edit"}
        autoComplete="off"
        initialValues={{ enabled: true, ...customer }}
        onFinish={(values) => {
          mutate({
            ...values,
            phone: values.phone?.toString(),
            taxNumber: values.taxNumber?.toString(),
            id: customer?.id,
          });
        }}
        layout="vertical"
      >
        <AppTabs
          selectedTab={activeTab}
          onChange={setActiveTab}
          tabs={tabItems.map((tab) => ({
            ...tab,
            Title: tab.label,
            Component: tab.children,
            key: tab.key,
            props: {
              form: form,
              customer: customer,
              initialValues: initialValues,
            },
          }))}
        />

        <StyledFlex
          style={{
            justifyContent: "end",
            marginTop: 24,
            marginBottom: 24,
          }}
        >
          <Space>
            <ActionButton
              type="reset"
              onClick={handleResetForm}
              loading={isPending}
            />

            <ActionButton type="save" loading={isPending} />
          </Space>
        </StyledFlex>
      </Form>
    </AppCard>
  );
};

export default CustomerDetailEdit;
